import {
  AfterViewInit,
  Directive,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FEATURE_TOGGLER, FeatureToggler } from '../feature-toggler';
/**
 * @ignore
 * Asserts that an object is not null or undefined
 * @param object Object to assert
 *
 * @return boolean
 */
function isNullOrUndefined(object: any): boolean {
  return object == null;
}

/**
 * @ignore
 * Asserts that a value is not null or undefined
 *
 * @param val Value to assert
 * @param errorMessage Message to display in error scenario
 *
 * @throws error when value is null or undefined
 */
export function verifyRequiredAttribute(val: any | any[], errorMessage: string): Error | null {
  if ((Array.isArray(val) && val.some(isNullOrUndefined)) || isNullOrUndefined(val)) {
    throw new Error(errorMessage);
  }
  return;
}

/**
 * Structural directive that can be used inline to turn on or off a specific
 * component. The directive must receive as main input a boolean flag or a
 * feature ID.
 *
 * If a boolean flag is provided, the directive may use that to toggle the
 * feature on or off. If a string is provided, it will look into the toggle
 * router service for the id.
 *
 * By default, if nothing else is provided to the directive, it may behave as a
 * *ngIf. But, it can receive an ng-template, which will be treated as an
 * experimental component. In this case, the directive will switch between the
 * experimental feature and the current component.
 */
@Directive({
  selector: '[cbreTogglePoint]',
  standalone: false,
})
export class TogglePointDirective implements OnInit, AfterViewInit, OnDestroy {
  private nonExperimentalRef: TemplateRef<any>;
  private destroy$ = new Subject<void>();
  private _featureId: string = null;

  @Input() public set cbreTogglePointReplaceWith(templateRef: TemplateRef<any> | null) {
    this.nonExperimentalRef = templateRef;
    this.updateView();
  }

  @Input('cbreTogglePoint') public set featureId(featureId: string) {
    verifyRequiredAttribute(featureId, 'featureId is a required attribute.');
    if (this._featureId !== featureId) {
      this._featureId = featureId;
      this.updateView();
    }
  }

  constructor(
    private viewContainer: ViewContainerRef,
    private experimentalRef: TemplateRef<any>,
    @Inject(FEATURE_TOGGLER) private toggleRouterService: FeatureToggler
  ) {}

  ngOnInit(): void {
    // Subscribe to feature state changes to update the view when context changes
    this.toggleRouterService.featureStateChanged$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !!this._featureId)
      )
      .subscribe(() => this.updateView());
  }

  ngAfterViewInit(): void {
    this.updateView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Clear view container to later, if enabled, create the experimental
   * embedded view. If experimental template exists, it will create it's
   * view or the non experimental view. if it doesn't exist, it will
   * consider the only view as the experimental.
   */
  private updateView(): void {
    const isEnabled = this.toggleRouterService.isFeatureEnabled(this._featureId);
    this.viewContainer.clear();

    if (!!this.nonExperimentalRef) {
      this.viewContainer.createEmbeddedView(isEnabled ? this.nonExperimentalRef : this.experimentalRef);
    } else if (isEnabled) {
      this.viewContainer.createEmbeddedView(this.experimentalRef);
    }
  }
}
